// Admin console landing: look up member accounts by the email they sign in
// with, then impersonate one. After a successful impersonation the app routes
// to '/', where the admin sees the member's session (ImpersonationBanner shows
// the active impersonation and an exit control).
import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import { either } from '../lib/utils/utils';
import {
	postAdminLookup,
	postAdminImpersonate,
	postAdminSession,
	postAdminLogout
} from './adminApi';
import { ADMIN_LOGIN_PATH } from './adminPath';

const errStr = e => (!e ? 'Network error.' : typeof e === 'string' ? e : JSON.stringify(e));

const AdminDashboard = ({ history }) => {
	const [email, setEmail] = useState('');
	const [results, setResults] = useState(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');

	// Guard the page: if the session is not an admin, send to the login.
	useEffect(() => {
		postAdminSession().then(
			either(
				() => history.push(ADMIN_LOGIN_PATH),
				s => {
					if (!s || !s.isAdmin) history.push(ADMIN_LOGIN_PATH);
				}
			)
		);
	}, [history]);

	const search = () => {
		setBusy(true);
		setError('');
		setResults(null);
		postAdminLookup(email.trim()).then(
			either(
				e => {
					setBusy(false);
					setError(errStr(e));
				},
				rows => {
					setBusy(false);
					setResults(rows || []);
				}
			)
		);
	};

	const impersonate = userId => {
		setBusy(true);
		setError('');
		postAdminImpersonate(userId).then(
			either(
				e => {
					setBusy(false);
					setError(errStr(e));
				},
				() => {
					setBusy(false);
					// Land on the app as the impersonated member.
					history.push('/dashboard/view');
				}
			)
		);
	};

	const logout = () => {
		postAdminLogout().then(
			either(
				() => history.push(ADMIN_LOGIN_PATH),
				() => history.push(ADMIN_LOGIN_PATH)
			)
		);
	};

	return (
		<Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: 2
				}}>
				<Typography variant="h5">Member Lookup</Typography>
				<Button onClick={logout}>Sign out</Button>
			</Box>

			<Paper sx={{ p: 2, mb: 3 }} elevation={2}>
				<Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
					<TextField
						fullWidth
						label="Sign-in email"
						value={email}
						onChange={e => setEmail(e.target.value)}
						onKeyPress={e => e.key === 'Enter' && search()}
					/>
					<Button
						variant="contained"
						disabled={!email.trim() || busy}
						onClick={search}>
						Search
					</Button>
				</Box>
			</Paper>

			{error ? (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			) : null}

			{busy ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
					<CircularProgress />
				</Box>
			) : null}

			{results && !busy ? (
				results.length === 0 ? (
					<Typography color="text.secondary">
						No accounts match that email.
					</Typography>
				) : (
					<Paper elevation={2}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Name</TableCell>
									<TableCell>Sign-in</TableCell>
									<TableCell>Email</TableCell>
									<TableCell>Member ID</TableCell>
									<TableCell>Org</TableCell>
									<TableCell align="right" />
								</TableRow>
							</TableHead>
							<TableBody>
								{results.map(r => (
									<TableRow key={r.id}>
										<TableCell>
											{r.firstName} {r.lastName}
											{r.suspended ? (
												<Chip
													size="small"
													color="warning"
													label="suspended"
													sx={{ ml: 1 }}
												/>
											) : null}
										</TableCell>
										<TableCell>{r.userName}</TableCell>
										<TableCell>{r.email}</TableCell>
										<TableCell>{r.memberID}</TableCell>
										<TableCell>{r.orgID}</TableCell>
										<TableCell align="right">
											<Button
												size="small"
												variant="outlined"
												disabled={r.suspended || busy}
												onClick={() =>
													impersonate(r.id)
												}>
												Sign in as
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Paper>
				)
			) : null}
		</Box>
	);
};

export default withRouter(AdminDashboard);
